import { format } from "date-fns";
import { FC, useCallback, useContext, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useParams } from "react-router-dom";

import {
  ABTestContentReportPageContainer_Query,
  ContentDevice,
} from "~/src/__relay_artifacts__/ABTestContentReportPageContainer_Query.graphql";
import { DateRangeContext } from "~/src/components/features/global/HeaderNavigation/DateRangeField";

import { ContentElementMetric, ContentReportPage } from "../ContentReportPage";

const query = graphql`
  query ABTestContentReportPageContainer_Query(
    $abtestSlug: String!
    $abtestGroupSlug: String!
    $input: ContentReportInput!
  ) {
    abtestScenarioPageGroup(slug: $abtestGroupSlug) {
      title
      page {
        url
      }
    }
    abtestScenarioPage(slug: $abtestSlug) {
      title
      reportContent(input: $input) {
        totalUniqueUsers
        totalViews
        totalGoals
        viewportWidth
        viewportHeight
        previewImageUrl
        elements {
          name
          previewImageUrl
          updatedDate
          views
          viewRate
          averageViewTime
          goals
          goalRate
          clicks
          clickRate
          position {
            x
            y
            width
            height
          }
        }
      }
    }
  }
`;

export const ABTestContentReportPageContainer: FC = () => {
  const {
    dateRange: [startOn, endOn],
  } = useContext(DateRangeContext);
  const { siteSlug = "", abtestSlug = "", patternSlug = "" } = useParams();
  const [deviceType, setDeviceType] = useState<ContentDevice>("PC");

  const handleDeviceChange = useCallback(
    (dt: string) => setDeviceType(dt as ContentDevice),
    []
  );

  const { abtestScenarioPage, abtestScenarioPageGroup } =
    useLazyLoadQuery<ABTestContentReportPageContainer_Query>(
      query,
      {
        abtestSlug: patternSlug,
        abtestGroupSlug: abtestSlug,
        input: {
          startOn: format(startOn, "yyyy-MM-dd"),
          endOn: format(endOn, "yyyy-MM-dd"),
          deviceType,
        },
      },
      {
        fetchKey: `${format(startOn, "yyyy-MM-dd")}-${format(
          endOn,
          "yyyy-MM-dd"
        )}-${deviceType}`,
        fetchPolicy: "network-only",
      }
    );

  if (!abtestScenarioPage) throw new Error("assertion failed");

  const breadcrumbs = useMemo(
    () => [
      { label: "A/Bテスト一覧", path: `/sites/${siteSlug}/abtests` },
      {
        label: "A/Bテスト詳細",
        path: `/sites/${siteSlug}/abtests/${abtestSlug}`,
      },
      {
        label: "パターン詳細",
        path: `/sites/${siteSlug}/abtests/${abtestSlug}/patterns/${patternSlug}`,
      },
      {
        label: "コンテンツレポート",
        path: `/sites/${siteSlug}/abtests/${abtestSlug}/patterns/${patternSlug}/content_report`,
      },
    ],
    [abtestSlug, patternSlug, siteSlug]
  );

  const elements = useMemo((): ContentElementMetric[] => {
    return (abtestScenarioPage.reportContent?.elements ?? []).map((el) => ({
      name: el.name,
      previewImageUrl: el.previewImageUrl,
      updatedDate: el.updatedDate,
      views: el.views,
      viewRate: el.viewRate,
      averageViewTime: el.averageViewTime,
      goals: el.goals,
      goalRate: el.goalRate,
      clicks: el.clicks,
      clickRate: el.clickRate,
      position: el.position
        ? {
            x: el.position.x,
            y: el.position.y,
            width: el.position.width,
            height: el.position.height,
          }
        : null,
    }));
  }, [abtestScenarioPage.reportContent?.elements]);

  return (
    <ContentReportPage
      title="A/Bテスト コンテンツレポート"
      breadcrumbs={breadcrumbs}
      pageUrl={abtestScenarioPageGroup?.page.url ?? ""}
      deviceType={deviceType}
      onDeviceChange={handleDeviceChange}
      totalUniqueUsers={abtestScenarioPage.reportContent?.totalUniqueUsers ?? 0}
      totalViews={abtestScenarioPage.reportContent?.totalViews ?? 0}
      totalGoals={abtestScenarioPage.reportContent?.totalGoals ?? 0}
      viewportWidth={abtestScenarioPage.reportContent?.viewportWidth}
      viewportHeight={abtestScenarioPage.reportContent?.viewportHeight}
      previewImageUrl={abtestScenarioPage.reportContent?.previewImageUrl}
      elements={elements}
    />
  );
};
